import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserPlus, LogIn, ArrowLeft, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth, hasRole } from "@/contexts/AuthContext";

/**
 * Public B2B submission is DISABLED per policy — only authenticated IBOs
 * can create B2B bills. This page shows a friendly landing screen.
 * If the visitor is already logged in as an IBO, we route them straight
 * to their portal at /b2b/my-bills.
 */
export default function B2BSubmit() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  React.useEffect(() => {
    if (loading || !user) return;
    // Signed-in IBOs land straight in their B2B portal.
    if (hasRole(user, "ibo", "admin")) {
      navigate("/b2b/my-bills", { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <span className="ml-auto text-sm font-medium">BDM Order Management</span>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-12 md:py-16">
        <div className="text-center space-y-3 mb-10">
          <div className="inline-flex items-center gap-1.5 text-xs font-medium bg-amber-100 text-amber-800 px-3 py-1 rounded-full">
            <ShieldCheck className="w-3.5 h-3.5" /> Members-only
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
            B2B Bill Creation is available to <span className="text-primary">IBO members</span>
          </h1>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            To create a B2B bill you need an IBO account. Sign in as an IBO to submit an
            auto-approved bill in seconds — or apply to become an IBO.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-6 space-y-3" data-testid="b2b-login-card">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <LogIn className="w-5 h-5" />
            </div>
            <h2 className="font-display font-semibold text-lg">Already an IBO?</h2>
            <p className="text-sm text-muted-foreground">
              Sign in to your IBO account and head to your B2B portal.
            </p>
            <Button className="w-full mt-2" onClick={() => navigate("/login")} data-testid="b2b-login-btn">
              <LogIn className="w-4 h-4 mr-1" /> Log in
            </Button>
          </Card>

          <Card className="p-6 space-y-3" data-testid="b2b-become-card">
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <UserPlus className="w-5 h-5" />
            </div>
            <h2 className="font-display font-semibold text-lg">Become an IBO</h2>
            <p className="text-sm text-muted-foreground">
              Sign up as an IBO to start creating B2B bills. Applications are reviewed
              within 1 business day.
            </p>
            <Button className="w-full mt-2" asChild data-testid="b2b-become-btn">
              <Link to="/ibo/register"><UserPlus className="w-4 h-4 mr-1" /> Register as IBO</Link>
            </Button>
            <Button variant="ghost" size="sm" className="w-full" asChild data-testid="b2b-track-btn">
              <Link to="/b2b/track">or Track an existing bill</Link>
            </Button>
          </Card>
        </div>

        <div className="mt-10 p-4 rounded-lg border border-primary/20 bg-primary/5 flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div className="text-sm text-primary/90">
            <b>Why the change?</b> B2B bills submitted by verified IBOs are auto-approved,
            skip admin queues, and are directly routed to the accountant for invoice upload —
            no waiting, no drop-off.
          </div>
        </div>
      </main>
    </div>
  );
}
